import t from '@jetshop/intl';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import { RangeFilterListItem } from '@jetshop/ui/Filter/FilterTypes/Range/RangeFilterFlyouts';
import Drawer, { DrawerTarget, DrawerTrigger } from '@jetshop/ui/Modal/Drawer';
import { SelectableListItem } from '@jetshop/ui/Select/Components';
import { TopListItem } from '@jetshop/ui/Select/SelectDropdown';
import SortOrderDropdown from '@jetshop/ui/SortOrder/SortOrderDropdown';
import theme from '@jetshop/ui/utils/theme';
import get from 'lodash.get';
import React, { Component, Fragment } from 'react';
import styled from 'react-emotion';
import { ActiveFilters } from './ActiveFilters';
import { BooleanFilters } from './BooleanFilters';
import FilterDrawer from './FilterDrawer';
import { ListFilters } from './ListFilters';
import { MultiListFilters } from './MultiListFilters';
import { RangeFilters } from './RangeFilters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ButtonWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  button:first-of-type {
    margin-right: ${theme('space.1')};
  }
  ${theme('above.sm')} {
    padding: ${theme('space.1')};
  }
  ${theme('below.lg')} {
    justify-content: space-between;
  }
`;

const MobileButton = styled('button')`
  height: 40px;
  background: #ffffff;
  border: 0;
  appearance: none;
  width: auto;
  cursor: pointer;
  text-align: left;
  padding: ${theme('space.1')};
  font-weight: ${theme('fontWeights.semibold')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme('colors.primary')};
  }

  p {
    margin-right: 10px;
  }
`;

const Filters = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  > div {
    margin-right: 0;
    margin-bottom: 0;
    ${TopListItem}, ${SelectableListItem}, ${RangeFilterListItem} {
      height: 40px;
      background-color: #f7f7f7;
    }

    button[data-flight-dropdown-button] {
      border: 0;
      border-radius: 0;
      appearance: none;
      width: auto;
      font-size: 1rem;
      font-weight: normal;
      &:focus {
        border: 0;
        outline: none;
      }
      svg {
        margin-left: 10px;
        margin-top: 1px;
        align-self: baseline;
      }

      &.active {
        svg {
          align-self: center;
          margin-top: -3px;
          use {
            fill: ${({ theme }) => theme.colors.primary};
          }
        }
      }

      + [data-flight-dropdown-items] {
        min-width: 200px;
        border: 1px solid #ddd;
        border-top: 0;
        border-radius: 0;
        left: 10px;
        max-height: 400px;
        overflow-y: scroll;

        &.range-slider-wrapper {
          overflow-y: hidden;
          border: 0;
          border-top: 1px solid #ddd;
          .handle {
            border-radius: 100%;
            background: ${({ theme }) => theme.colors.primary};
          }
          .slider {
            .bar-1:after {
              background: ${({ theme }) => theme.colors.primary};
            }
            + button {
              background: ${({ theme }) => theme.colors.primary};
            height: 40px;
            line-height: 40px;
            padding: 0;
            }
          }
        }
      }

    }
  }

  label {
    align-self: center;
  }

  .filter-title {
    font-weight: bold;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-right: 20px;
    svg {
      font-size: 1.5rem;
      width: 24px;
      margin-right: 10px;
    }
  }

  [data-flight-dropdown-item] {
    &:focus, &:hover {
      color: ${({ theme }) => theme.colors.primary}!important;
    }
    svg {
      use {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }
  }

`;

const FiltersSectionWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const FiltersWapper = styled('div')`
  width: auto;
`;
const BoolSortWrapper = styled('div')`
  display: flex;
  align-items: center;

  .filter-title {
    font-weight: bold;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-right: 0;
    svg {
      font-size: 1.5rem;
      width: 24px;
      margin-right: 10px;
    }
  }

  > div {
    width: auto;
    margin: 0;
    > button {
      height: 40px;
      background-color: transparent;
      width: auto;
      border: 0;
      padding-right: 0;
      h4 {
        font-size: 1rem;
        font-weight: normal;
        padding-right: 5px;
        text-transform: none;
        margin-left: 5px;
        font-family: ${({ theme }) => theme.fonts.primary};
      }

      &:focus {
        border: 0;
      }

      svg {
        margin-right: 0;
        transition: none;
      }

      + div {
        min-width: 180px;
        right: 0;
      }
    }
  }
`;

const ProductNumber = styled('p')`
  text-transform: uppercase;
  font-size: 12px;
  color: #878787;
  letter-spacing: 0.5px;
  align-self: baseline;
  word-break: initial;
  width: 120px;
  ${theme('below.md')} {
    text-align: center;
    width: 100%;
    margin: auto;
    margin-bottom: 10px;so
  }
`;

const FilterInfoWrapper = styled('div')`
  display: flex;
  margin-bottom: 2rem;

  ${theme('below.md')} {
    margin-bottom: 10px;
    display: block;
  }
`;

class Filter extends Component {
  render() {
    const { filters } = this.props;
    const sortOrders = get(this, 'props.sortOrders', []);

    return (
      <Fragment>
        <Above
          breakpoint="md"
          render={() => (
            <Fragment>
              <FiltersSectionWrapper>
                <FiltersWapper>
                  {filters?.length > 0 && (
                    <>
                      <Filters>
                        <span className="filter-title">
                          <FontAwesomeIcon icon={['fal', 'filter']} />
                          Filtrera på:
                        </span>
                        <ListFilters filters={filters} />
                        <RangeFilters filters={filters} />
                        <BooleanFilters filters={filters} />
                      </Filters>
                      <Filters>
                        <MultiListFilters filters={filters} />
                      </Filters>
                    </>
                  )}
                </FiltersWapper>
                <BoolSortWrapper>
                  {sortOrders.length > 0 && (
                    <>
                      <span className="filter-title">
                        <FontAwesomeIcon icon={['fal', 'sort-alpha-down']} />
                        Sorterar på:
                      </span>
                      <SortOrderDropdown sortOrders={sortOrders} />
                    </>
                  )}
                </BoolSortWrapper>
              </FiltersSectionWrapper>
              <FilterInfoWrapper>
                <ProductNumber>
                  {t('{totalProducts} Products', {
                    totalProducts: this.props.total
                  })}
                </ProductNumber>
                <ActiveFilters filters={filters} />
              </FilterInfoWrapper>
            </Fragment>
          )}
        />
        <Below
          breakpoint="md"
          render={() => {
            return (
              <Fragment>
                <ButtonWrapper>
                  {filters?.length > 0 && (
                    <DrawerTrigger id="filterDrawer">
                      {({ showTarget }) => (
                        <MobileButton onClick={showTarget}>
                          <p>{t('Filter')}</p>
                          <FontAwesomeIcon icon={['fal', 'filter']} />
                        </MobileButton>
                      )}
                    </DrawerTrigger>
                  )}

                  {sortOrders.length > 0 && (
                    <DrawerTrigger id="sortDrawer">
                      {({ showTarget }) => (
                        <MobileButton onClick={showTarget}>
                          <p>{t('Sort By')}</p>
                          <FontAwesomeIcon icon={['fal', 'sort-alpha-down']} />
                        </MobileButton>
                      )}
                    </DrawerTrigger>
                  )}
                </ButtonWrapper>

                <FilterInfoWrapper>
                  <ProductNumber>
                    {t('{totalProducts} Products', {
                      totalProducts: this.props.total
                    })}
                  </ProductNumber>
                  <ActiveFilters filters={filters} />
                </FilterInfoWrapper>

                <DrawerTarget id="filterDrawer">
                  {({ hideTarget, isOpen }) => (
                    <Drawer isOpen={isOpen} left>
                      <FilterDrawer close={hideTarget} filters={filters} />
                    </Drawer>
                  )}
                </DrawerTarget>

                {sortOrders.length > 0 && (
                  <DrawerTarget id="sortDrawer">
                    {({ hideTarget, isOpen }) => (
                      <Drawer isOpen={isOpen} right>
                        <FilterDrawer
                          close={hideTarget}
                          sortOrders={sortOrders}
                        />
                      </Drawer>
                    )}
                  </DrawerTarget>
                )}
              </Fragment>
            );
          }}
        />
      </Fragment>
    );
  }
}

export default Filter;
